import React, { useState } from 'react';
import { MarketingPage } from './types';
import { useMarketingData } from './MarketingContext';

interface Props {
  currentPage: MarketingPage;
  onNavigate: (page: MarketingPage) => void;
  onBackToPortal: () => void;
  children: React.ReactNode;
}

const NAV_ITEMS: { id: MarketingPage; label: string; icon: string; group: string }[] = [
  { id: 'marketing-dashboard', label: 'Dashboard', icon: '📊', group: 'Overview' },
  { id: 'marketing-companies', label: 'Companies', icon: '🏢', group: 'Planning' },
  { id: 'marketing-projects', label: 'Projects', icon: '📋', group: 'Planning' },
  { id: 'marketing-products', label: 'Products', icon: '📦', group: 'Planning' },
  { id: 'marketing-strategy', label: 'Three-Year Strategy', icon: '🎯', group: 'Strategy' },
  { id: 'marketing-campaigns', label: 'Campaigns', icon: '📣', group: 'Strategy' },
  { id: 'marketing-content', label: 'Content Calendar', icon: '📅', group: 'Execution' },
  { id: 'marketing-seo', label: 'SEO Center', icon: '🔍', group: 'Execution' },
  { id: 'marketing-research', label: 'Market Research', icon: '🔬', group: 'Research' },
  { id: 'marketing-knowledge', label: 'Knowledgebase', icon: '📚', group: 'Research' },
  { id: 'marketing-standards', label: 'Industry Standards', icon: '📏', group: 'Research' },
  { id: 'marketing-blogs', label: 'Blogs & Articles', icon: '📰', group: 'Research' },
  { id: 'marketing-analytics', label: 'Analytics & Reports', icon: '📈', group: 'Reports' },
  { id: 'marketing-documents', label: 'Documentation', icon: '📝', group: 'Reports' },
  { id: 'marketing-tasks', label: 'Tasks & Approvals', icon: '✅', group: 'Operations' },
  { id: 'marketing-settings', label: 'Settings', icon: '⚙️', group: 'Operations' },
];

export default function MarketingLayout({ currentPage, onNavigate, onBackToPortal, children }: Props) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { notifications } = useMarketingData();
  const unreadCount = notifications.filter(n => !n.read).length;

  const groups = [...new Set(NAV_ITEMS.map(i => i.group))];

  const pageLabels: Record<MarketingPage, string> = {
    'marketing-dashboard': 'Executive Dashboard',
    'marketing-companies': 'Companies',
    'marketing-projects': 'Projects',
    'marketing-products': 'Products',
    'marketing-strategy': 'Three-Year Strategy',
    'marketing-campaigns': 'Campaigns',
    'marketing-content': 'Content Calendar',
    'marketing-seo': 'SEO Center',
    'marketing-research': 'Market Research',
    'marketing-knowledge': 'Knowledgebase',
    'marketing-standards': 'Industry Standards',
    'marketing-blogs': 'Blogs & Articles',
    'marketing-analytics': 'Analytics & Reports',
    'marketing-documents': 'Documentation Center',
    'marketing-tasks': 'Tasks & Approvals',
    'marketing-settings': 'Settings',
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-white border-r border-gray-200 z-40 transition-all duration-200 ${sidebarOpen ? 'w-64' : 'w-16'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Brand */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-gray-100">
          {sidebarOpen ? (
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold">M</div>
              <div>
                <div className="text-sm font-bold text-gray-900 leading-tight">Marketing Portal</div>
                <div className="text-[10px] text-gray-400 font-medium">SKI Industries</div>
              </div>
            </div>
          ) : (
            <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold mx-auto">M</div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="hidden lg:block p-1 text-gray-400 hover:text-gray-600">
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Back to Portal */}
        <div className="px-3 pt-3">
          <button onClick={onBackToPortal} className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}>
            <span>🏠</span>
            {sidebarOpen && <span>Back to Portal</span>}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {groups.map(group => {
            const items = NAV_ITEMS.filter(i => i.group === group);
            return (
              <div key={group} className="mb-4">
                {sidebarOpen && (
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-3 mb-1.5">{group}</div>
                )}
                {items.map(item => {
                  const active = currentPage === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { onNavigate(item.id); setMobileOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg mb-0.5 transition-colors ${active ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'} ${!sidebarOpen ? 'justify-center' : ''}`}
                      title={item.label}
                    >
                      <span className="text-base">{item.icon}</span>
                      {sidebarOpen && <span className="truncate">{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-200 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-20 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg">☰</button>
            <div className="text-sm text-gray-500 hidden sm:block">
              <span className="text-gray-400">Marketing / </span>
              <span className="font-medium text-gray-700">{pageLabels[currentPage]}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Global Search */}
            <button onClick={() => setSearchOpen(!searchOpen)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Search">
              🔍
            </button>
            {/* Notifications */}
            <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Notifications">
              🔔
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount}</span>
              )}
            </button>
          </div>
        </header>

        {/* Search Bar */}
        {searchOpen && (
          <div className="bg-white border-b border-gray-200 px-4 py-3">
            <div className="max-w-2xl mx-auto relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search companies, projects, campaigns, content, tasks..."
                className="w-full px-4 py-2.5 pl-10 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              <span className="absolute left-3 top-3 text-gray-400 text-sm">🔍</span>
              {searchQuery && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                  <div className="p-3 text-sm text-gray-500">
                    Search results for "{searchQuery}" will appear here. Connect to a search provider or implement client-side filtering.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
